import { useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

import apiClient from 'services/api';
import { lerToken } from 'services/sessao';

// Mesmo esquema de usePerfilProfissional: cache em memória por sessão do app (reseta só num F5)
// mais um mapa de requisições em voo, para que NavBar e AdminRoute — que montam juntos em toda
// navegação dentro de /admin — não disparem a mesma pergunta duas vezes.
const cache = new Map();
const emAndamento = new Map();

// Quem está montado com useIsAdmin agora. revalidarAdmin() avisa a todos de uma vez: a navegação e
// os cards da Saúde leem a mesma resposta, e têm que mudar juntos.
const ouvintes = new Set();

/**
 * Pede a todo useIsAdmin montado que pergunte de novo, sem desmontar nada (sem F5).
 *
 * Existe para a volta da API. `'indisponivel'` não entra no cache, mas o hook só perguntava na
 * montagem, então a resposta continuava valendo depois que a API voltava: o menu ficava só com
 * "Saúde" e os cards escondidos até um F5 manual. Quem já tem `true`/`false` no cache sai dele, sem
 * requisição; perguntas simultâneas se juntam no `emAndamento`.
 */
export function revalidarAdmin() {
  ouvintes.forEach((avisar) => avisar());
}

function buscarSeEhAdmin(userId) {
  if (cache.has(userId)) {
    return Promise.resolve(cache.get(userId));
  }

  if (!emAndamento.has(userId)) {
    const promise = apiClient
      .get('/admin/me')
      .then((response) => {
        const valor = response?.data?.admin === true;
        cache.set(userId, valor);
        return valor;
      })
      .finally(() => {
        emAndamento.delete(userId);
      });
    emAndamento.set(userId, promise);
  }

  return emAndamento.get(userId);
}

/**
 * Indica se o usuário logado tem acesso administrativo (GET /admin/me).
 *
 * Quem decide é o backend, a partir de app.admin.usuario-ids — antes o NavBar decidia sozinho com
 * "Number(userId) === 1", e o id do administrador existia em dois lugares que podiam discordar em
 * silêncio: o menu sumia para quem tinha acesso, ou aparecia para quem tomava 403 ao usá-lo.
 *
 * Retorna null enquanto não resolveu — mesmo contrato de usePerfilProfissional. Quem consome trata
 * o null como "ainda não sei" e não pode chutar: mostrar o menu antes da resposta o faria piscar a
 * cada troca de página, e escondê-lo cedo demais bloquearia o admin de verdade.
 *
 * Quatro valores, não três: `null` (perguntando), `true`, `false` (o backend respondeu que não:
 * 401/403, qualquer status abaixo de 500) e `'indisponivel'` (o backend NÃO respondeu). A distinção
 * existe por causa de /admin/health, a tela que serve justamente para o caso de a API estar fora —
 * tratar "sem resposta" como "não é admin" a mandaria para o dashboard na única hora em que ela
 * importa. `'indisponivel'` não entra no cache: a próxima montagem pergunta de novo. Quem compara
 * com `=== true` (NavBar) não vê diferença.
 *
 * "Não respondeu" inclui 5xx, e isso não é detalhe: com o Spring fora, quem responde ao /api/admin/me
 * é o nginx (em produção) ou o setupProxy (em dev), e os dois devolvem 502 — uma resposta HTTP, com
 * `erro.response` preenchido. Olhar só para a ausência de resposta deixava a Saúde inalcançável
 * exatamente no cenário para o qual ela existe.
 */
export function useIsAdmin(userId) {
  const [isAdmin, setIsAdmin] = useState(() => (
    userId && cache.has(userId) ? cache.get(userId) : null
  ));
  const [rodada, setRodada] = useState(0);

  useEffect(() => {
    const avisar = () => setRodada((n) => n + 1);
    ouvintes.add(avisar);
    return () => { ouvintes.delete(avisar); };
  }, []);

  // Numa revalidação o valor atual fica na tela até a resposta nova chegar (não volta a null): o
  // AdminRoute trocaria a Saúde por "Carregando…" a cada tentativa.
  useEffect(() => {
    if (!userId) {
      setIsAdmin(null);
      return undefined;
    }

    if (cache.has(userId)) {
      setIsAdmin(cache.get(userId));
      return undefined;
    }

    let ativo = true;
    buscarSeEhAdmin(userId)
      .then((valor) => {
        if (ativo) setIsAdmin(valor);
      })
      .catch((erro) => {
        // 401/403 é o backend dizendo não. Sem resposta, ou 5xx (o 502 do nginx/proxy com o Spring
        // fora), é outra coisa — e não pode travar a tela em carregamento para sempre: vira
        // 'indisponivel', e o AdminRoute decide o que fazer (dashboard, exceto em /admin/health).
        const status = erro?.response?.status;
        if (ativo) setIsAdmin(status && status < 500 ? false : 'indisponivel');
      });

    return () => { ativo = false; };
  }, [userId, rodada]);

  return isAdmin;
}

/**
 * useIsAdmin para o usuário da sessão atual: lê o token e extrai o userId sozinho. Sem token, ou com
 * um token ilegível, devolve null, o mesmo "ainda não sei" de useIsAdmin sem userId.
 *
 * É o que a área administrativa usa para decidir o que desenhar na Saúde, a única seção que abre sem
 * administrador confirmado (ver AdminRoute em App.jsx).
 */
export function useIsAdminDaSessao() {
  const token = lerToken();
  const userId = useMemo(() => {
    if (!token) return null;
    try {
      return jwtDecode(token).userId;
    } catch (error) {
      return null;
    }
  }, [token]);

  return useIsAdmin(userId);
}
